import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { BodyL, HeadlineL } from '@sorare/core/src/atoms/typography';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { GameweekDate } from 'components/onboarding/GameweekDate';
import { MarketGuidanceLayout } from 'components/onboarding/MarketGuidanceLayout';

import { Game } from './Game';
import type { TeamsSelection_so5League } from './__generated__/index.graphql';

const Games = styled(Vertical).attrs({ gap: 1.5 })`
  @media ${tabletAndAbove} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  }
`;
const SkeletonContent = styled.div`
  width: 340px;
  height: 130px;
  border-radius: var(--double-unit);
`;
const WhiteText = styled.span`
  color: var(--c-white);
`;
const StyledImg = styled.img`
  width: calc(6 * var(--unit));
  height: calc(6 * var(--unit));
  object-fit: contain;
`;

type Props<T extends TeamsSelection_so5League> = {
  so5League?: T;
  loading?: boolean;
  onSubmit: (
    teams: (
      | NonNullable<T['games'][number]['homeTeam']>
      | NonNullable<T['games'][number]['awayTeam']>
    )[]
  ) => void;
};
export const TeamsSelection = <T extends TeamsSelection_so5League>({
  so5League,
  loading,
  onSubmit,
}: Props<T>) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  const [selectedTeams, setSelectedTeams] = useState<
    (
      | NonNullable<T['games'][number]['homeTeam']>
      | NonNullable<T['games'][number]['awayTeam']>
    )[]
  >([]);

  const buttonDisabled = selectedTeams.length < 3;

  return (
    <MarketGuidanceLayout
      cta={
        <Button
          disabled={buttonDisabled}
          fullWidth={!isTabletAndAbove}
          color="primary"
          size="medium"
          onClick={() => onSubmit(selectedTeams)}
        >
          <FormattedMessage
            id="TeamsSelection.cta"
            defaultMessage="{x, plural, =0 {Pick 3 Teams} =1 {Pick 2 More Teams} =2 {Pick 1 More Teams} other {Confirm # picks}}"
            values={{
              x: selectedTeams.length,
            }}
          />
        </Button>
      }
    >
      <Vertical center={isTabletAndAbove} gap={2}>
        <Vertical center={isTabletAndAbove}>
          {so5League && (
            <StyledImg src={so5League.logoUrl} alt={so5League.name} />
          )}
          <HeadlineL>
            <FormattedMessage
              id="TeamsSelection.title"
              defaultMessage="Let's find the right players"
            />
          </HeadlineL>
          <BodyL color="var(--c-nd-600)">
            <FormattedMessage
              id="TeamsSelection.subtitle"
              defaultMessage="Pick at least 3 clubs you think will smash it in the next Game Week <b>({gameweekDate})</b>."
              values={{
                gameweekDate: (
                  <GameweekDate so5Fixture={so5League?.so5Fixture} />
                ),
                b: (chunks: ReactNode[]) => {
                  return <WhiteText>{chunks}</WhiteText>;
                },
              }}
            />
          </BodyL>
        </Vertical>
        <Games>
          {loading ? (
            <>
              <Skeleton loading>
                <SkeletonContent />
              </Skeleton>
              <Skeleton loading>
                <SkeletonContent />
              </Skeleton>
              <Skeleton loading>
                <SkeletonContent />
              </Skeleton>
            </>
          ) : (
            so5League?.games.map(game => (
              <Game
                key={game.id}
                game={game}
                selectedTeams={selectedTeams}
                onSelectTeam={team => {
                  setSelectedTeams(previous => {
                    const newSelectedTeams = [...previous];
                    const index = previous.findIndex(
                      ({ slug }) => slug === team.slug
                    );
                    if (index > -1) {
                      newSelectedTeams.splice(index, 1);
                    } else {
                      newSelectedTeams.push(team);
                    }

                    return newSelectedTeams;
                  });
                }}
              />
            ))
          )}
        </Games>
      </Vertical>
    </MarketGuidanceLayout>
  );
};

TeamsSelection.fragments = {
  so5League: gql`
    fragment TeamsSelection_so5League on So5League {
      slug
      name
      logoUrl
      games {
        id
        ...Game_game
      }
      so5Fixture {
        slug
        ...GameweekDate_so5Fixture
      }
    }
    ${GameweekDate.fragments.so5Fixture}
    ${Game.fragments.game}
  ` as TypedDocumentNode<TeamsSelection_so5League>,
};
